import { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";

import { Box, Button, CircularProgress, Typography } from "@mui/material";

import { centeredBoxSx } from "../styles/centeredBoxSx";
import { idpHintQueryParams, readIdpHint } from "./idpHint";
import { sanitizedCurrentUrl } from "./sanitizedCurrentUrl";
import { setTokenAccessor } from "./tokenAccessor";

interface AuthGuardProps extends PropsWithChildren {
  /**
   * The brokered identity-provider alias this app accepts in the `?idp=` entry marker, forwarded to
   * Keycloak as `kc_idp_hint`. Omitted — the default — means the marker is ignored, which is what the
   * portal app wants: its realm brokers no identity provider at all (ADR-0017).
   */
  brokeredIdpAlias?: string;
}

export const AuthGuard = ({ children, brokeredIdpAlias }: AuthGuardProps) => {
  const auth = useAuth();
  const { t } = useTranslation();
  const redirectAttempted = useRef(false);

  // Read once, at mount, from the URL the user arrived on: that same URL is about to be sanitized into
  // redirect_uri, and the retry below must still know which provider the entry link named.
  const [idpHint] = useState<string | null>(() => (brokeredIdpAlias ? readIdpHint(brokeredIdpAlias) : null));

  const signIn = useCallback(
    () =>
      void auth.signinRedirect({
        // Return to the exact page the user opened so a deep link survives the Keycloak round-trip.
        redirect_uri: sanitizedCurrentUrl(),
        // Skips Keycloak's provider chooser when the user came from the FSW portal; undefined
        // otherwise, which leaves the request unchanged.
        extraQueryParams: idpHintQueryParams(idpHint),
      }),
    [auth, idpHint]
  );

  useEffect(() => {
    setTokenAccessor(() => auth.user);
    return () => setTokenAccessor(null);
  }, [auth.user]);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !redirectAttempted.current) {
      redirectAttempted.current = true;
      signIn();
    }
  }, [auth.isLoading, auth.isAuthenticated, signIn]);

  if (auth.isLoading) {
    return (
      <Box sx={centeredBoxSx}>
        <CircularProgress />
        <Typography>{t("auth.loading")}</Typography>
      </Box>
    );
  }

  if (auth.isAuthenticated) {
    return <>{children}</>;
  }

  if (auth.error) {
    return (
      <Box sx={centeredBoxSx}>
        <Typography color="error">{t("auth.connectionError")}</Typography>
        <Button variant="contained" onClick={signIn}>
          {t("auth.retry")}
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={centeredBoxSx}>
      <CircularProgress />
      <Typography>{t("auth.redirecting")}</Typography>
    </Box>
  );
};
